import richTextInitialHtml from "./rich_text_initial_html"

const THEMES = ["brand", "muted", "success", "warning", "lime", "error"]

const INLINE_RICH_TEXT_SANITIZE = {
  b: true,
  strong: true,
  i: true,
  em: true,
  u: true,
  code: true,
  mark: true,
  br: true,
  div: true,
  a: {
    href: true,
    target: true,
    rel: true
  },
  span: {
    class: true,
    style: true
  },
  font: {
    color: true
  }
}

function normalizeInitialItems(raw) {
  const rows = Array.isArray(raw) ? raw : []
  if (rows.length === 0) return [{ content: "", items: [] }]

  return rows.map((row) => {
    if (row && typeof row === "object" && "content" in row) {
      const content = typeof row.content === "string" ? row.content : ""
      return {
        content: richTextInitialHtml(content),
        items: []
      }
    }

    return {
      content: richTextInitialHtml(row == null ? "" : String(row)),
      items: []
    }
  })
}

export default class HighlightListTool {
  static get enableLineBreaks() {
    return true
  }

  static get toolbox() {
    return {
      title: "Highlight list",
      icon: "<span class=\"icon-[lucide--list-ordered] size-4\"></span>"
    }
  }

  static get sanitize() {
    return {
      theme: false,
      items: {
        content: INLINE_RICH_TEXT_SANITIZE
      }
    }
  }

  constructor({ data, readOnly } = {}) {
    this.readOnly = Boolean(readOnly)
    this.data = {
      theme: this.normalizeTheme(data?.theme),
      items: normalizeInitialItems(data?.items)
    }
    this.themeButtons = new Map()
    this.wrapper = null
    this.cardsEl = null
    this.addButton = null
  }

  render() {
    this.wrapper = document.createElement("div")
    this.wrapper.className = "editorjs-highlight-list"

    this.cardsEl = document.createElement("div")
    this.cardsEl.className = "editorjs-highlight-list__cards"

    this.data.items.forEach((row) => {
      this.cardsEl.appendChild(this.createListCard(row.content || ""))
    })
    this.updateItemNumbers()

    this.addButton = document.createElement("button")
    this.addButton.type = "button"
    this.addButton.className = "editorjs-highlight-list__add btn btn-ghost btn-xs mt-2"
    this.addButton.textContent = "Add item"
    if (this.readOnly) {
      this.addButton.classList.add("hidden")
    }
    this.addButton.addEventListener("click", () => {
      this.cardsEl.appendChild(this.createListCard(""))
      this.updateItemNumbers()
      this.applyThemeClass()
    })

    this.wrapper.append(this.cardsEl, this.addButton)
    this.applyThemeClass()
    return this.wrapper
  }

  createListCard(initialHtml) {
    const ol = document.createElement("ol")
    ol.className = "editorjs-highlight-list__card"

    const li = document.createElement("li")
    li.className = "editorjs-highlight-list__item"

    const row = document.createElement("div")
    row.className = "editorjs-highlight-list__item-row"

    const number = document.createElement("span")
    number.className = "editorjs-highlight-list__number"
    number.setAttribute("aria-hidden", "true")

    const editor = document.createElement("div")
    editor.className = "editorjs-highlight-list__item-text"
    editor.contentEditable = this.readOnly ? "false" : "true"
    editor.innerHTML = initialHtml || ""

    row.append(number, editor)

    if (!this.readOnly) {
      const removeBtn = document.createElement("button")
      removeBtn.type = "button"
      removeBtn.className = "editorjs-highlight-list__remove btn btn-ghost btn-xs btn-square shrink-0"
      removeBtn.setAttribute("aria-label", "Remove item")
      removeBtn.textContent = "×"
      removeBtn.addEventListener("click", () => {
        if (this.cardsEl.querySelectorAll(":scope > .editorjs-highlight-list__card").length <= 1) return
        ol.remove()
        this.updateItemNumbers()
      })
      row.appendChild(removeBtn)
    }

    li.appendChild(row)
    ol.appendChild(li)
    return ol
  }

  renderSettings() {
    const settings = document.createElement("div")
    settings.className = "editorjs-highlight-list-settings"

    THEMES.forEach((theme) => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "editorjs-highlight-list-settings__btn"
      button.textContent = this.themeLabel(theme)
      this.themeButtons.set(theme, button)

      button.addEventListener("click", () => {
        this.data.theme = theme
        this.updateSettingsButtonsState()
        this.applyThemeClass()
      })

      settings.appendChild(button)
    })

    this.updateSettingsButtonsState()
    return settings
  }

  save() {
    const items = []
    this.cardsEl.querySelectorAll(":scope > .editorjs-highlight-list__card .editorjs-highlight-list__item-text").forEach((editor) => {
      items.push({
        content: editor.innerHTML || "",
        items: []
      })
    })

    return {
      theme: this.normalizeTheme(this.data.theme),
      items
    }
  }

  validate(savedData) {
    const items = Array.isArray(savedData?.items) ? savedData.items : []
    for (const item of items) {
      const html = item?.content || ""
      const tmp = document.createElement("div")
      tmp.innerHTML = html
      if ((tmp.textContent || "").trim()) return true
    }
    return false
  }

  updateItemNumbers() {
    this.cardsEl.querySelectorAll(":scope > .editorjs-highlight-list__card .editorjs-highlight-list__number").forEach((el, index) => {
      el.textContent = String(index + 1)
    })
  }

  applyThemeClass() {
    if (!this.cardsEl) return

    const theme = this.normalizeTheme(this.data.theme)
    this.cardsEl.querySelectorAll(":scope > .editorjs-highlight-list__card").forEach((card) => {
      THEMES.forEach((themeName) => {
        card.classList.remove(`editorjs-highlight-list--${themeName}`)
      })
      card.classList.add(`editorjs-highlight-list--${theme}`)
    })
  }

  updateSettingsButtonsState() {
    this.themeButtons.forEach((button, theme) => {
      button.classList.toggle("editorjs-highlight-list-settings__btn--active", this.data.theme === theme)
    })
  }

  normalizeTheme(theme) {
    return THEMES.includes(theme) ? theme : "brand"
  }

  themeLabel(theme) {
    switch (theme) {
    case "brand":
      return "Brand"
    case "muted":
      return "Muted"
    case "success":
      return "Success"
    case "warning":
      return "Warning"
    case "lime":
      return "Lime"
    case "error":
      return "Error"
    default:
      return "Brand"
    }
  }
}
